// Journey-Stage Adaptive Dashboards — Vendor Home.
//
// Vendors land here (WS-0 made Home the default). The dashboard is
// stage-adaptive: a journey spine + a single "next best action" tuned to
// where the vendor is (still qualifying → get connected → building the
// relationship → actively servicing), plus the widgets that matter for that
// stage.
//
// STAGE IS SERVER-CLASSIFIED (audit F-25): the qualification read carries
// `stage` from PortalJourneyStages.Vendor.Classify — the old client-side
// derivation (which proxied "reporting" via borrower sign-offs and had
// diverged) is gone. The FE only narrows the server string with
// `asVendorStage`.
//
// PER-WIDGET FETCHING (audit F-10): the qualification read is the ONE
// primary fetch gating the page (PortalHomeLayout). Customers and invoices
// are hoisted tab-level `useWidgetData` fetches (each shared between the
// next-best-action and its card — one fetch, two consumers) that only fire
// for stages whose widgets need them; tax forms fetch inside their own
// widget. One failed fetch degrades only its card — spine, hero, and
// qualification always render.

import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, StatusBadge } from "@/design-system/components/ui";
import type { StatusTone } from "@/design-system/components/ui";
import { formatCompactCurrency, formatCurrency } from "@/design-system/lib/format";
import {
  JourneySpine,
  NextBestActionCard,
  StatTile,
  StatTileRow,
  type WidgetTone,
} from "../dashboard/DashboardPrimitives";
import {
  PortalHomeDashboard,
  PortalHomeLayout,
  WidgetFrame,
  useWidgetData,
  type PortalHomeRecipe,
  type WidgetData,
} from "../dashboard/engine";
import { VENDOR_SPINE, asVendorStage, vendorSpine, type VendorStage } from "../dashboard/journey";
import {
  portalVendorApi,
  type PortalVendorInvoiceDto,
  type PortalVendorQualificationDto,
  type VendorQualificationStatus,
} from "../api/vendor";
import {
  portalVendorCustomersApi,
  ENGAGEMENT_STATUS_LABEL,
  type EngagementStatus,
  type VendorCustomerDto,
} from "../api/connections";
import { taxFormsApi, type TaxForm } from "../api/taxForms";

// ── Shared vocab / formatting ────────────────────────────────────────────────

const OUTSTANDING_STATUSES = ["issued", "viewed", "overdue"] as const;
const ENGAGEMENT_ORDER: EngagementStatus[] = [
  "new",
  "contacted",
  "in-negotiation",
  "under-contract",
];
const QUALIFIED_STATUSES: VendorQualificationStatus[] = [
  "complete",
  "waived",
  "not-applicable",
];

const STAGE_LABELS: Record<string, string> = {
  "background-check": "Background check",
  "insurance-verification": "Insurance verification",
  "reference-checks": "Reference checks",
  "capability-assessment": "Capability assessment",
  "w9-collection": "W-9 collection",
};

/** Prose money — the next-best-action sentence, where a vendor may be
 *  reconciling the figure against an invoice and it must stay exact. */
const money = (amount: number): string =>
  formatCurrency(amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Tile money — abbreviates only past $10,000, so ordinary invoice figures
 *  still read exactly while a large one shortens instead of being clipped. */
const tileMoney = formatCompactCurrency;

const plural = (n: number): string => (n === 1 ? "" : "s");

function stageLabel(code: string): string {
  return STAGE_LABELS[code] ?? code.replace(/-/g, " ");
}

function isQualDone(status: VendorQualificationStatus): boolean {
  return QUALIFIED_STATUSES.includes(status);
}

function qualTone(status: VendorQualificationStatus): StatusTone {
  switch (status) {
    case "complete":
      return "ok";
    case "in-progress":
      return "progress";
    case "waived":
    case "not-applicable":
      return "muted";
    default:
      return "neutral";
  }
}

function qualLabel(status: VendorQualificationStatus): string {
  switch (status) {
    case "in-progress":
      return "In progress";
    case "complete":
      return "Complete";
    case "waived":
      return "Waived";
    case "not-applicable":
      return "N/A";
    default:
      return "Pending";
  }
}

function prettyFormStatus(status: string): string {
  const s = status.replace(/[-_]/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function nextIncompleteLabel(q: PortalVendorQualificationDto): string | null {
  const next = q.stages.find((s) => !isQualDone(s.status));
  return next ? stageLabel(next.stageCode) : null;
}

// ── Per-fetch summaries (pure) ───────────────────────────────────────────────

interface InvoiceSummary {
  hasBillable: boolean;
  outstanding: number;
  overdueSum: number;
  overdueCount: number;
  processing: number;
  collected: number;
}

function summarizeInvoices(invoices: PortalVendorInvoiceDto[]): InvoiceSummary {
  const billable = invoices.filter((i) => i.status !== "void");
  let outstanding = 0;
  let overdueSum = 0;
  let overdueCount = 0;
  let processing = 0;
  let collected = 0;
  for (const inv of billable) {
    if (inv.status === "paid") collected += inv.amount;
    else if (inv.status === "processing") processing += inv.amount;
    else if (OUTSTANDING_STATUSES.includes(inv.status as (typeof OUTSTANDING_STATUSES)[number]))
      outstanding += inv.amount;
    if (inv.status === "overdue") {
      overdueSum += inv.amount;
      overdueCount += 1;
    }
  }
  return {
    hasBillable: billable.length > 0,
    outstanding,
    overdueSum,
    overdueCount,
    processing,
    collected,
  };
}

interface CustomerSummary {
  customerCount: number;
  withContract: number;
  withoutContract: number;
  engagementCounts: Record<EngagementStatus, number>;
}

function summarizeCustomers(customers: VendorCustomerDto[]): CustomerSummary {
  const connections = customers.flatMap((c) => c.loans);
  const engagementCounts: Record<EngagementStatus, number> = {
    "new": 0,
    "contacted": 0,
    "in-negotiation": 0,
    "under-contract": 0,
  };
  let withContract = 0;
  for (const l of connections) {
    engagementCounts[l.engagementStatus] += 1;
    if (l.contract != null) withContract += 1;
  }
  return {
    customerCount: customers.length,
    withContract,
    withoutContract: connections.length - withContract,
    engagementCounts,
  };
}

// ── Widget context ───────────────────────────────────────────────────────────

interface VendorHomeCtx {
  qualification: PortalVendorQualificationDto;
  stage: VendorStage;
  /** Hoisted tab-level fetch — shared by the next-best-action and the
   *  Customers card. Null data = stage-gated off (no request made). */
  customers: WidgetData<VendorCustomerDto[] | null>;
  /** Hoisted tab-level fetch — shared by the next-best-action and the
   *  Invoicing card. Null data = stage-gated off (no request made). */
  invoices: WidgetData<PortalVendorInvoiceDto[] | null>;
  goToTab: (tab: string) => void;
}

// ── Next best action ─────────────────────────────────────────────────────────

interface NbaSpec {
  heading: string;
  description: string;
  tone: WidgetTone;
  action?: { label: string; tab: string };
}

function nextBestAction(ctx: VendorHomeCtx): NbaSpec {
  const { stage, qualification } = ctx;
  switch (stage) {
    case "onboarding": {
      // Advisory honesty (audit F-36): Phoenix records these steps — nothing
      // is asked of the vendor and no step gates getting connected.
      const next = nextIncompleteLabel(qualification);
      return {
        heading: "Your qualification is underway",
        description: next
          ? `Next up: ${next}. Phoenix records these steps for you — nothing is needed from you, and we'll update your progress here as each one clears.`
          : "Phoenix is recording your qualification steps — check back soon.",
        tone: "default",
      };
    }
    case "portal-active":
      return {
        heading: "You're qualified — Phoenix connects you from here",
        description:
          "Phoenix connects you with borrowers who need your services. We'll notify you the moment a customer connects.",
        tone: "success",
      };
    case "connected": {
      const c = ctx.customers.status === "ready" && ctx.customers.data
        ? summarizeCustomers(ctx.customers.data)
        : null;
      if (c == null || c.customerCount === 0)
        // Customer details unavailable (fetch failed / still loading) —
        // neutral relationship copy, never invented counts.
        return {
          heading: "Keep building your customer relationships",
          description:
            "Track engagement and upload signed agreements from Customers.",
          tone: "default",
          action: { label: "View customers", tab: "customers" },
        };
      return c.withoutContract > 0
        ? {
            heading: "Move your connections under contract",
            description: `You're connected to ${c.customerCount} customer${plural(
              c.customerCount,
            )}. ${c.withoutContract} connection${plural(c.withoutContract)} still ${
              c.withoutContract === 1 ? "needs" : "need"
            } a signed agreement.`,
            tone: "default",
            action: { label: "View customers", tab: "customers" },
          }
        : {
            heading: "Keep building your customer relationships",
            description: `You're connected to ${c.customerCount} customer${plural(
              c.customerCount,
            )}. Track engagement and upload signed agreements from Customers.`,
            tone: "success",
            action: { label: "View customers", tab: "customers" },
          };
    }
    case "servicing": {
      // Regulated vendors (audit F-35): their service fees are billed to the
      // borrower through Phoenix — never nudge them toward invoices.
      if (qualification.isRegulated)
        return {
          heading: "Keep your monthly reports current",
          description:
            "Reports keep customers and Phoenix in the loop. Fees for your services are billed through Phoenix — there are no invoices for you to chase.",
          tone: "success",
          action: { label: "Go to reports", tab: "reports" },
        };
      const inv = ctx.invoices.status === "ready" ? ctx.invoices.data : null;
      if (inv == null)
        // Invoice status unavailable — never claim all-clear.
        return {
          heading: "Keep your monthly reports current",
          description:
            ctx.invoices.status === "error"
              ? "Reports keep customers and Phoenix in the loop. We couldn't check your invoice status right now."
              : "Reports keep customers and Phoenix in the loop.",
          tone: "default",
          action: { label: "Go to reports", tab: "reports" },
        };
      const s = summarizeInvoices(inv);
      if (s.overdueCount > 0)
        return {
          heading: `${s.overdueCount} invoice${plural(s.overdueCount)} overdue`,
          description: `${money(
            s.overdueSum,
          )} is past due across your customers. Follow up so payments keep flowing.`,
          tone: "warning",
          action: { label: "View invoices", tab: "invoices" },
        };
      if (s.outstanding > 0)
        return {
          heading: "Invoices awaiting payment",
          description: `${money(s.outstanding)} is outstanding across your customers.`,
          tone: "default",
          action: { label: "View invoices", tab: "invoices" },
        };
      return {
        heading: "You're all caught up",
        description:
          "Nothing's overdue. Keep your monthly reports current so customers and Phoenix stay in the loop.",
        tone: "success",
        action: { label: "Go to reports", tab: "reports" },
      };
    }
  }
}

// ── Widgets ──────────────────────────────────────────────────────────────────

function QualificationWidget({
  qualification,
  span,
  advisory,
}: {
  qualification: PortalVendorQualificationDto;
  span?: "full" | "wide";
  advisory?: boolean;
}) {
  return (
    <WidgetFrame
      title={advisory ? "Qualification (advisory)" : "Your readiness"}
      span={span}
      testId="vendor-home-qualification"
    >
      <ul className="space-y-space-2 list-none p-0 m-0">
        {qualification.stages.map((s) => (
          <li
            key={s.stageCode}
            className="flex items-center justify-between gap-space-3"
            data-testid={`vendor-home-qual-${s.stageCode}`}
          >
            <span className="text-small text-onyx">{stageLabel(s.stageCode)}</span>
            <StatusBadge tone={qualTone(s.status)} struck={s.status === "waived"}>
              {qualLabel(s.status)}
            </StatusBadge>
          </li>
        ))}
      </ul>
      {!advisory && (
        <p className="text-caption text-gray-mid m-0">
          Phoenix records these — you don't need to do anything, and none of
          them hold up your connections. We'll let you know as each step clears.
        </p>
      )}
    </WidgetFrame>
  );
}

function CustomersEmptyWidget() {
  return (
    <WidgetFrame title="Customers" testId="vendor-home-customers-empty">
      {/* Step 5 W1 — kept in step with the Customers tab's empty state. Being
          assigned to a loan does not open the connection; a separate staff
          action does, so "once Phoenix connects you" is the honest framing
          and the two surfaces must not tell different stories. */}
      <p className="text-small text-gray-mid m-0">
        No customers connected yet. Phoenix opens the connection once your
        assignment is finalised — we'll notify you when it does.
      </p>
    </WidgetFrame>
  );
}

function CustomersWidget({ ctx }: { ctx: VendorHomeCtx }) {
  const { customers } = ctx;
  const summary = customers.data ? summarizeCustomers(customers.data) : null;
  return (
    <WidgetFrame
      title="Customers"
      testId="vendor-home-customers"
      state={customers}
      action={
        <Button variant="text" size="sm" onClick={() => ctx.goToTab("customers")}>
          View all customers
        </Button>
      }
    >
      {summary && (
        <>
          {/* THREE tiles, not four. "Connections" was dropped because it is the
              exact sum of the two that remain — a number the card already
              states twice — and because "connections" is Phoenix's internal
              join concept, not how a vendor thinks about their own business.
              What is left is where they stand, the positive, and the action. */}
          <StatTileRow fluid>
            <StatTile
              label="Customers"
              value={summary.customerCount}
              testId="vendor-home-customer-count"
            />
            <StatTile
              label="Under contract"
              value={summary.withContract}
              tone={summary.withContract > 0 ? "success" : "default"}
            />
            <StatTile
              label="Need agreement"
              value={summary.withoutContract}
              tone={summary.withoutContract > 0 ? "warning" : "default"}
              testId="vendor-home-need-agreement"
            />
          </StatTileRow>
          <div className="flex flex-wrap gap-space-2">
            {ENGAGEMENT_ORDER.filter((k) => summary.engagementCounts[k] > 0).map((k) => (
              <StatusBadge key={k} tone="neutral">
                {ENGAGEMENT_STATUS_LABEL[k]}: {summary.engagementCounts[k]}
              </StatusBadge>
            ))}
          </div>
        </>
      )}
    </WidgetFrame>
  );
}

function InvoicingWidget({ ctx }: { ctx: VendorHomeCtx }) {
  const { invoices, qualification } = ctx;
  const summary = invoices.data ? summarizeInvoices(invoices.data) : null;
  return (
    <WidgetFrame
      title="Invoicing"
      testId="vendor-home-invoicing"
      state={invoices}
      action={
        <Button variant="text" size="sm" onClick={() => ctx.goToTab("invoices")}>
          View all invoices
        </Button>
      }
    >
      {summary?.hasBillable ? (
        // THREE tiles, not four. "Overdue" was dropped on the no-repeating-the-
        // banner rule: whenever it is non-zero the next-best-action card
        // directly above LEADS with exactly that count and sum ("N invoices
        // overdue" / "$X is past due"), and whenever it is zero the tile said
        // nothing. The amount is still inside Outstanding either way, and the
        // Invoices tab carries the overdue count with every overdue invoice
        // listed. A vendor cannot hold an overdue invoice outside the servicing
        // stage — PortalJourneyStages.Vendor.Classify returns Servicing for ANY
        // post-draft non-void invoice — so the banner is always the one
        // carrying it.
        <StatTileRow fluid>
          <StatTile
            label="Outstanding"
            value={tileMoney(summary.outstanding)}
            testId="vendor-home-outstanding"
          />
          <StatTile
            label="In transit"
            value={tileMoney(summary.processing)}
            hint="Clearing"
          />
          <StatTile
            label="Collected"
            value={tileMoney(summary.collected)}
            tone="success"
            testId="vendor-home-collected"
          />
        </StatTileRow>
      ) : qualification.isRegulated ? (
        // Audit F-35: a regulated vendor's fees are billed to the BORROWER
        // through Phoenix — invoices will never show up here, so don't
        // promise they will.
        <p className="text-small text-gray-mid m-0">
          Fees for your services are billed through Phoenix — you won't see
          invoices here.
        </p>
      ) : (
        <p className="text-small text-gray-mid m-0">
          No invoices on file yet. Amounts you bill through Phoenix will show up
          here.
        </p>
      )}
    </WidgetFrame>
  );
}

function TaxFormsWidget({ ctx }: { ctx: VendorHomeCtx }) {
  const forms = useWidgetData<TaxForm[]>(() => taxFormsApi.list(), []);
  return (
    <WidgetFrame
      title="1099s"
      testId="vendor-home-taxforms"
      state={forms}
      action={
        forms.data != null &&
        forms.data.length > 0 && (
          <Button variant="text" size="sm" onClick={() => ctx.goToTab("tax-forms")}>
            Open
          </Button>
        )
      }
    >
      {forms.data != null &&
        (forms.data.length === 0 ? (
          <p className="text-small text-gray-mid m-0">
            No 1099s available yet. Your annual tax forms will appear here when
            Phoenix sends them.
          </p>
        ) : (
          <ul className="space-y-space-2 list-none p-0 m-0">
            {forms.data.map((f) => (
              <li key={f.id} className="flex items-center justify-between gap-space-3">
                <span className="text-small text-onyx">
                  {f.formType} · {f.taxYear}
                </span>
                <StatusBadge tone="info">{prettyFormStatus(f.status)}</StatusBadge>
              </li>
            ))}
          </ul>
        ))}
    </WidgetFrame>
  );
}

// ── Recipe ───────────────────────────────────────────────────────────────────

const VENDOR_HOME_RECIPE: PortalHomeRecipe<VendorHomeCtx> = {
  widgets: [
    {
      key: "qualification",
      stages: ["onboarding", "portal-active"],
      render: (ctx) => (
        <QualificationWidget qualification={ctx.qualification} span="full" />
      ),
    },
    {
      key: "qualification-advisory",
      stages: ["connected", "servicing"],
      hidden: (ctx) => nextIncompleteLabel(ctx.qualification) == null,
      render: (ctx) => <QualificationWidget qualification={ctx.qualification} advisory />,
    },
    {
      key: "customers-teaser",
      stages: ["portal-active"],
      render: () => <CustomersEmptyWidget />,
    },
    {
      key: "customers",
      stages: ["connected", "servicing"],
      render: (ctx) => <CustomersWidget ctx={ctx} />,
    },
    {
      key: "invoicing",
      stages: ["servicing"],
      render: (ctx) => <InvoicingWidget ctx={ctx} />,
    },
    {
      key: "tax-forms",
      stages: ["servicing"],
      render: (ctx) => <TaxFormsWidget ctx={ctx} />,
    },
  ],
};

// ── Component ────────────────────────────────────────────────────────────────

export function VendorHomeTab() {
  const [, setParams] = useSearchParams();

  const goToTab = useCallback(
    (tab: string) => {
      setParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          updated.set("tab", tab);
          return updated;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  // PRIMARY fetch — the only one that gates the page. Carries the
  // server-classified stage (F-25) + the regulated flag (F-35).
  const qualification = useWidgetData<PortalVendorQualificationDto>(
    () => portalVendorApi.getQualification(),
    [],
  );
  const stage: VendorStage = asVendorStage(qualification.data?.stage) ?? "onboarding";

  // Hoisted stage-gated fetches (F-10): each fires only for the stages whose
  // widgets consume it; a gated-off stage resolves null with NO request.
  const customers = useWidgetData<VendorCustomerDto[] | null>(
    () =>
      stage === "connected" || stage === "servicing"
        ? portalVendorCustomersApi.list()
        : Promise.resolve(null),
    [stage],
  );
  const invoices = useWidgetData<PortalVendorInvoiceDto[] | null>(
    () => (stage === "servicing" ? portalVendorApi.listInvoices() : Promise.resolve(null)),
    [stage],
  );

  const data = qualification.data;
  if (!data) {
    return (
      <PortalHomeLayout
        loading={qualification.status === "loading"}
        error={qualification.status === "error" ? qualification.error : null}
        onRetry={qualification.reload}
        testId="vendor-home"
      />
    );
  }

  const ctx: VendorHomeCtx = { qualification: data, stage, customers, invoices, goToTab };
  const pos = vendorSpine(stage);
  const nba = nextBestAction(ctx);

  return (
    <PortalHomeLayout stage={stage} testId="vendor-home">
      <JourneySpine
        steps={VENDOR_SPINE}
        currentIndex={pos.index}
        complete={pos.complete}
        testId="vendor-home-spine"
      />

      <NextBestActionCard
        heading={nba.heading}
        description={nba.description}
        tone={nba.tone}
        testId="vendor-home-nba"
        actions={
          nba.action && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => goToTab(nba.action!.tab)}
              data-testid="vendor-home-nba-action"
            >
              {nba.action.label}
            </Button>
          )
        }
      />

      <PortalHomeDashboard
        recipe={VENDOR_HOME_RECIPE}
        ctx={ctx}
        stage={stage}
        testId="vendor-home-grid"
      />
    </PortalHomeLayout>
  );
}
